import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faSearch, faCheck, faPlus, faTimes, faEye, faMagic, faCommentDots, faPen,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { Button, Form, Modal, Spinner } from '@themesberg/react-bootstrap';
import api from '../../utils/api';

const PRESETS = [
  { id: 'merhaba', label: 'Merhaba 👋' },
  { id: 'nasilsin', label: 'Nasılsın?' },
  { id: 'tanisalim', label: 'Tanışalım mı?' }
];

const SEQUENCE_KEY = 'bimbo-bulk-reply-sequence';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function delayFor(mode) {
  if (mode === '0') return 0;
  if (mode === '1') return 1000;
  if (mode === '3') return 3000;
  return 1000 + Math.floor(Math.random() * 2000);
}

function locative(city) {
  if (!city || city === '-') return '';
  const name = String(city).trim();
  if (!name) return '';
  const lower = name.toLocaleLowerCase('tr-TR');
  const vowels = 'aıoueiöü';
  let lastVowel = '';
  for (let i = lower.length - 1; i >= 0; i -= 1) {
    if (vowels.includes(lower[i])) {
      lastVowel = lower[i];
      break;
    }
  }
  const hard = 'fstkçşhp'.includes(lower[lower.length - 1]);
  const back = 'aıou'.includes(lastVowel);
  const suffix = hard ? (back ? 'ta' : 'te') : (back ? 'da' : 'de');
  return `${name}'${suffix}`;
}

function firstName(fullName) {
  const first = String(fullName || '').trim().split(/\s+/)[0] || '';
  if (!first) return 'Üye';
  return first.charAt(0).toLocaleUpperCase('tr-TR') + first.slice(1).toLocaleLowerCase('tr-TR');
}

function fillPreview(template, row) {
  if (!template) return '';
  const name = firstName(row?.memberName);
  const city = row?.memberCity || 'Şehir';
  return String(template)
    .replace(/\{\{\s*first_uye_name\s*\}\}/gi, name)
    .replace(/\{\{\s*uye_ad\s*\}\}/gi, name)
    .replace(/\{\{\s*uye_sehir_da\s*\}\}/gi, locative(city) || city)
    .replace(/\{\{\s*uye_sehir(_adi)?\s*\}\}/gi, city)
    .replace(/\{\{\s*onayli_ad\s*\}\}/gi, row?.botName || '')
    .replace(/\{\{\s*onayli_yas\s*\}\}/gi, row?.botAge != null ? String(row.botAge) : '');
}

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Europe/Istanbul'
  });
}

function Face({ src, name }) {
  if (src) {
    return <img src={src} alt="" className="brf-face" />;
  }
  return <span className="brf-face brf-face-fallback">{(name || 'U').charAt(0).toUpperCase()}</span>;
}

function rowTime(row) {
  const time = new Date(row?.date || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function duplicateRowIds(list) {
  const byMember = new Map();
  list.forEach((row) => {
    const member = String(row.memberId || '');
    if (!member) return;
    if (!byMember.has(member)) byMember.set(member, []);
    byMember.get(member).push(row);
  });
  const extra = new Set();
  byMember.forEach((memberRows) => {
    const rooms = new Map();
    memberRows.forEach((row) => {
      const room = String(row.roomId || row.id);
      const time = rowTime(row);
      const prev = rooms.get(room);
      if (prev == null || time > prev) rooms.set(room, time);
    });
    if (rooms.size < 2) return;
    let keepRoom = null;
    let keepTime = -1;
    rooms.forEach((time, room) => {
      if (time > keepTime) {
        keepTime = time;
        keepRoom = room;
      }
    });
    memberRows.forEach((row) => {
      if (String(row.roomId || row.id) !== keepRoom) extra.add(row.id);
    });
  });
  return extra;
}

function selectionFor(list, collapse) {
  const ids = new Set(list.map((row) => row.id));
  if (collapse) duplicateRowIds(list).forEach((id) => ids.delete(id));
  return ids;
}

function loadSequence() {
  try {
    const saved = JSON.parse(localStorage.getItem(SEQUENCE_KEY) || '[]');
    if (Array.isArray(saved) && saved.length > 0) return saved.map((line) => String(line));
  } catch (err) {
    return ['', '', ''];
  }
  return ['', '', ''];
}

export default function BulkReplyModal({ show, onHide, quickMessages, onDone, onOpenChat }) {
  const [audience, setAudience] = useState('unpaid');
  const [preset, setPreset] = useState('merhaba');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(false);
  const [replyMode, setReplyMode] = useState('plain');
  const [pickedId, setPickedId] = useState(null);
  const [manualText, setManualText] = useState('');
  const [sequence, setSequence] = useState(loadSequence);
  const [intervalMode, setIntervalMode] = useState('0');
  const [collapseSameMember, setCollapseSameMember] = useState(true);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const cancelRef = useRef(false);
  const queryRef = useRef(null);
  const collapseRef = useRef(true);
  collapseRef.current = collapseSameMember;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    localStorage.setItem(SEQUENCE_KEY, JSON.stringify(sequence));
  }, [sequence]);

  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    setLoading(true);
    setResult(null);
    api.post('/admin-messages/unanswered/bulk-filter', {
      preset,
      query: debouncedQuery,
      audience
    }).then((res) => {
      if (!alive) return;
      const list = res.data?.rows || [];
      setRows(list);
      setTruncated(!!res.data?.truncated);
      setSelected(selectionFor(list, collapseRef.current));
      setExpanded(false);
    }).catch(() => {
      if (!alive) return;
      setRows([]);
      setSelected(new Set());
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [show, preset, debouncedQuery, audience]);

  const plainMessages = useMemo(
    () => (quickMessages || []).filter((msg) => !msg.isSmart && (msg.content || msg.text)),
    [quickMessages]
  );
  const smartMessages = useMemo(
    () => (quickMessages || []).filter((msg) => msg.isSmart && (msg.content || msg.text)),
    [quickMessages]
  );
  const activeList = replyMode === 'smart' ? smartMessages : plainMessages;
  const picked = activeList.find((msg) => msg.id === pickedId) || null;

  const templates = useMemo(() => {
    if (audience === 'paid') {
      return sequence.map((line) => line.trim()).filter(Boolean);
    }
    if (replyMode === 'manual') return manualText.trim() ? [manualText.trim()] : [];
    const body = (picked && (picked.content || picked.text)) || '';
    return body.trim() ? [body.trim()] : [];
  }, [audience, sequence, replyMode, manualText, picked]);

  const duplicateIds = useMemo(() => duplicateRowIds(rows), [rows]);

  const orderedRows = useMemo(() => {
    if (!collapseSameMember || duplicateIds.size === 0) return rows;
    const extras = [];
    const rest = [];
    rows.forEach((row) => {
      if (duplicateIds.has(row.id)) extras.push(row);
      else rest.push(row);
    });
    return [...extras, ...rest];
  }, [rows, collapseSameMember, duplicateIds]);

  const selectedRows = useMemo(
    () => orderedRows.filter((row) => selected.has(row.id)),
    [orderedRows, selected]
  );

  const assignment = useMemo(() => {
    const map = new Map();
    if (templates.length === 0) return map;
    selectedRows.forEach((row, index) => {
      const slot = index % templates.length;
      map.set(row.id, { n: slot + 1, text: templates[slot] });
    });
    return map;
  }, [selectedRows, templates]);

  const visibleRows = expanded ? orderedRows : orderedRows.slice(0, 8);
  const hiddenCount = Math.max(0, rows.length - visibleRows.length);
  const previewRow = selectedRows[0] || rows[0];
  const previewText = previewRow && templates[0] ? fillPreview(templates[0], previewRow) : '';

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((row) => row.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addSequenceFrom = (msg) => {
    const body = (msg && (msg.content || msg.text)) || '';
    if (!body) return;
    setSequence((prev) => [...prev, body]);
  };

  const send = async () => {
    const queue = selectedRows.slice();
    if (sending || queue.length === 0 || templates.length === 0) return;
    cancelRef.current = false;
    setSending(true);
    setResult(null);
    let sent = 0;
    let failed = 0;
    for (let i = 0; i < queue.length; i += 1) {
      if (cancelRef.current) break;
      const row = queue[i];
      const plan = assignment.get(row.id);
      setProgress({ current: i + 1, total: queue.length, name: row.memberName });
      try {
        await api.post('/admin-messages/unanswered/bulk-send', {
          roomId: row.roomId,
          botId: row.botId,
          memberId: row.memberId,
          memberName: row.memberName,
          memberCity: row.memberCity,
          template: plan.text,
          notify: i === 0 || queue[i - 1].roomId !== row.roomId
        });
        sent += 1;
        setRows((prev) => prev.filter((item) => item.id !== row.id));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      } catch (err) {
        failed += 1;
      }
      if (i < queue.length - 1 && !cancelRef.current) {
        await sleep(delayFor(intervalMode));
      }
    }
    setSending(false);
    setProgress(null);
    const stopped = cancelRef.current;
    setResult(
      `${sent} mesaj gönderildi` +
      (failed ? `, ${failed} gönderilemedi` : '') +
      (stopped ? '. Gönderim durduruldu.' : '.')
    );
    if (sent > 0 && onDone) onDone();
  };

  return (
    <Modal show={show} onHide={sending ? undefined : onHide} centered dialogClassName="brf-dialog" contentClassName="brf-content">
      <style>{`
        .brf-dialog { max-width: 980px; width: calc(100vw - 2rem); }
        .brf-content { border: 0; border-radius: 18px; overflow: hidden; }
        .brf-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 18px 22px 8px; }
        .brf-title { display: flex; gap: 10px; align-items: flex-start; }
        .brf-title h5 { margin: 0; font-weight: 700; }
        .brf-title p { margin: 2px 0 0; color: #6c757d; font-size: 13px; }
        .brf-body { padding: 8px 22px 16px; background: #f7f9fc; max-height: calc(100vh - 180px); overflow: auto; }
        .brf-audience { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
        .brf-audience button { text-align: left; border-radius: 12px; border: 1px solid #d7dee8; background: #fff; padding: 10px 12px; }
        .brf-audience button strong { display: block; }
        .brf-audience button span { color: #6c757d; font-size: 12px; }
        .brf-audience button.on { border-color: #2f6fed; background: #eef4ff; box-shadow: inset 0 0 0 1px #2f6fed; }
        .brf-card { background: #fff; border: 1px solid #e6ebf2; border-radius: 14px; padding: 14px; margin-bottom: 12px; }
        .brf-step { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin-bottom: 10px; }
        .brf-step h6 { margin: 0; font-size: 15px; }
        .brf-step p { margin: 2px 0 0; color: #6c757d; font-size: 12px; }
        .brf-num { width: 22px; height: 22px; border-radius: 50%; background: #2f6fed; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; margin-right: 8px; }
        .brf-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .brf-chip { border: 1px solid #d7dee8; background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 13px; font-weight: 600; }
        .brf-chip.on { background: #2f6fed; color: #fff; border-color: #2f6fed; }
        .brf-count { background: #e8f7ee; color: #157347; border-radius: 12px; padding: 8px 12px; font-size: 13px; min-width: 180px; }
        .brf-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .brf-table th { text-align: left; color: #6c757d; font-weight: 600; font-size: 12px; padding: 6px; border-bottom: 1px solid #eef2f6; }
        .brf-table td { padding: 8px 6px; border-bottom: 1px solid #f2f4f8; vertical-align: middle; }
        .brf-person { display: flex; align-items: center; gap: 8px; }
        .brf-face { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
        .brf-face-fallback { display: inline-flex; align-items: center; justify-content: center; background: #d7dee8; color: #334; font-weight: 700; }
        .brf-age { color: #6c757d; font-weight: 500; margin-left: 4px; }
        .brf-more { width: 100%; border: 0; background: #f3f6fb; color: #2f6fed; border-radius: 10px; padding: 8px; margin-top: 8px; font-weight: 600; }
        .brf-modes { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
        .brf-mode { border: 1px solid #d7dee8; background: #fff; border-radius: 10px; padding: 8px 12px; font-weight: 600; }
        .brf-mode.on { border-color: #2f6fed; color: #2f6fed; background: #eef4ff; }
        .brf-seq { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
        .brf-seq span { width: 28px; height: 28px; border-radius: 8px; background: #eef4ff; color: #2f6fed; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; flex: 0 0 auto; }
        .brf-foot { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 22px 16px; background: #fff; border-top: 1px solid #e6ebf2; }
        .brf-preview { color: #6c757d; font-size: 12px; }
        .brf-slot { display: inline-block; min-width: 22px; text-align: center; background: #eef4ff; color: #2f6fed; border-radius: 6px; font-weight: 700; font-size: 12px; padding: 2px 4px; }
        .brf-warn { background: #fff8e8; }
        .brf-bang { color: #b45309; margin-left: 6px; }
        .brf-collapse { display: flex; align-items: flex-start; gap: 8px; margin-top: 10px; font-size: 13px; }
        .brf-collapse small { display: block; color: #6c757d; font-weight: 400; }
      `}</style>
      <div className="brf-head">
        <div className="brf-title">
          <FontAwesomeIcon icon={faPaperPlane} className="text-primary mt-1" />
          <div>
            <h5>Filtrele ve Toplu Cevapla</h5>
            <p>Aynı mesajı gönderen bekleyen sohbetleri süzün ve tek işlemle yanıtlayın.</p>
          </div>
        </div>
        <Button variant="link" className="text-secondary p-0" onClick={onHide} disabled={sending}>
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </div>
      <div className="brf-body">
        <div className="brf-audience">
          <button type="button" className={audience === 'unpaid' ? 'on' : ''} onClick={() => setAudience('unpaid')} disabled={sending}>
            <strong>Satın almamış</strong>
            <span>Bekleyen sohbetlere aynı cevap gider</span>
          </button>
          <button type="button" className={audience === 'paid' ? 'on' : ''} onClick={() => setAudience('paid')} disabled={sending}>
            <strong>Satın almış</strong>
            <span>Mesaj sırası zamana göre dağıtılır, bitince başa döner</span>
          </button>
        </div>

        <div className="brf-card">
          <div className="brf-step">
            <div>
              <h6><span className="brf-num">1</span>Mesaj filtresi</h6>
              <p>Hangi mesajı yazan üyeler gelsin?</p>
            </div>
            <div className="brf-count">
              <strong>{loading ? '...' : rows.length} konuşma bulundu</strong>
              <div>{preset === 'manual' ? (query || 'Manuel arama') : PRESETS.find((item) => item.id === preset)?.label}</div>
            </div>
          </div>
          <div className="brf-chips mb-2">
            {PRESETS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`brf-chip ${preset === item.id ? 'on' : ''}`}
                onClick={() => setPreset(item.id)}
                disabled={sending}
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              className={`brf-chip ${preset === 'manual' ? 'on' : ''}`}
              onClick={() => {
                setPreset('manual');
                setTimeout(() => queryRef.current?.focus(), 0);
              }}
              disabled={sending}
            >
              <FontAwesomeIcon icon={faPen} className="me-1" />
              Mesaj içeriği manuel gir
            </button>
          </div>
          <div className="position-relative">
            <FontAwesomeIcon icon={faSearch} className="position-absolute text-muted" style={{ left: 12, top: 11 }} />
            <Form.Control
              ref={queryRef}
              value={query}
              placeholder={preset === 'manual' ? 'Aranacak mesaj metnini yazın' : 'İsterseniz metnin içinde de arayın'}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 34 }}
              disabled={sending}
            />
          </div>
          <Form.Check
            className="brf-collapse"
            type="checkbox"
            id="brf-collapse-same"
            checked={collapseSameMember}
            disabled={sending}
            onChange={(e) => {
              const on = e.target.checked;
              setCollapseSameMember(on);
              setSelected((prev) => {
                const next = new Set(prev);
                duplicateIds.forEach((id) => {
                  if (on) next.delete(id);
                  else next.add(id);
                });
                return next;
              });
            }}
            label={(
              <span>
                Aynı üyeyi 1 mesaja düşür
                <small>
                  Bu kümede aynı üye birden fazla sohbetteyse fazlalık üstte, seçimsiz ve ünlemli durur.
                  {collapseSameMember && duplicateIds.size > 0 ? ` ${duplicateIds.size} sohbet seçilmedi.` : ''}
                </small>
              </span>
            )}
          />
          {truncated && <div className="text-warning small mt-2">Liste 500 kayıtla sınırlandı. İlk 500 kayıt gösteriliyor.</div>}
        </div>

        <div className="brf-card">
          <div className="brf-step">
            <div>
              <h6><span className="brf-num">2</span>Filtrelenen konuşmalar</h6>
              <p>
                {audience === 'paid'
                  ? 'Her sohbet odası bir kez listelenir. Mesaj sütununda o odanın son mesajı vardır.'
                  : 'Satın almamış ve hâlâ cevap bekleyen sohbetler. Her oda bir kez, son mesajıyla.'}
              </p>
            </div>
            <Form.Check
              type="checkbox"
              checked={rows.length > 0 && selected.size === rows.length}
              onChange={toggleAll}
              label={`${selected.size}/${rows.length} seçili`}
              disabled={sending || rows.length === 0}
            />
          </div>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Aranıyor...</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-4">Bu filtreye uyan bekleyen mesaj yok.</div>
          ) : (
            <>
              <table className="brf-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }} />
                    <th>Gönderen</th>
                    <th>Alıcı</th>
                    <th>Son mesaj</th>
                    <th>Tarih</th>
                    {audience === 'paid' && <th>Sıra</th>}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const plan = assignment.get(row.id);
                    const warned = collapseSameMember && duplicateIds.has(row.id);
                    return (
                      <tr key={row.id} className={warned ? 'brf-warn' : ''}>
                        <td>
                          <Form.Check
                            checked={selected.has(row.id)}
                            onChange={() => toggleOne(row.id)}
                            disabled={sending}
                          />
                        </td>
                        <td>
                          <div className="brf-person">
                            <Face src={row.memberAvatar} name={row.memberName} />
                            <div>
                              <strong>{row.memberName}</strong>
                              {row.memberAge ? <span className="brf-age">{row.memberAge}</span> : null}
                              {warned && (
                                <span className="brf-bang" title="Aynı üye bu filtrede birden fazla sohbette. Seçilmedi; iki kez mesaj gitmesin.">
                                  <FontAwesomeIcon icon={faExclamationTriangle} />
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="brf-person">
                            <Face src={row.botAvatar} name={row.botName} />
                            <div>
                              <strong>{row.botName}</strong>
                              {row.botAge ? <span className="brf-age">{row.botAge}</span> : null}
                            </div>
                          </div>
                        </td>
                        <td>{row.message}</td>
                        <td className="text-muted">{formatTime(row.date)}</td>
                        {audience === 'paid' && (
                          <td title={plan ? plan.text : ''}>
                            {plan ? <span className="brf-slot">{plan.n}</span> : '—'}
                          </td>
                        )}
                        <td>
                          <Button variant="link" size="sm" className="p-0" title="Sohbeti aç" onClick={() => onOpenChat && onOpenChat(row)}>
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {hiddenCount > 0 && (
                <button type="button" className="brf-more" onClick={() => setExpanded(true)}>
                  +{hiddenCount} konuşma daha
                </button>
              )}
            </>
          )}
        </div>

        <div className="brf-card">
          <div className="brf-step">
            <div>
              <h6><span className="brf-num">3</span>{audience === 'paid' ? 'Mesaj sırası' : 'Cevap'}</h6>
              <p>
                {audience === 'paid'
                  ? 'Her sohbet odasına bir cevap gider. 5 metin ve 80 sohbet varsa 6. sohbet yine 1. metni alır.'
                  : 'Seçilen her sohbete aynı cevap, o sohbetin onaylı hesabından gider.'}
              </p>
            </div>
            <Form.Select
              value={intervalMode}
              onChange={(e) => setIntervalMode(e.target.value)}
              style={{ maxWidth: 180 }}
              disabled={sending}
            >
              <option value="0">Ara vermeden</option>
              <option value="1">1 sn ara</option>
              <option value="1-3">1–3 sn ara</option>
              <option value="3">3 sn ara</option>
            </Form.Select>
          </div>

          {audience === 'unpaid' ? (
            <>
              <div className="brf-modes">
                <button type="button" className={`brf-mode ${replyMode === 'plain' ? 'on' : ''}`} onClick={() => setReplyMode('plain')}>
                  <FontAwesomeIcon icon={faCommentDots} className="me-1" /> Hızlı mesajlar
                </button>
                <button type="button" className={`brf-mode ${replyMode === 'smart' ? 'on' : ''}`} onClick={() => setReplyMode('smart')}>
                  <FontAwesomeIcon icon={faMagic} className="me-1" /> Akıllı mesajlar
                </button>
                <button type="button" className={`brf-mode ${replyMode === 'manual' ? 'on' : ''}`} onClick={() => setReplyMode('manual')}>
                  <FontAwesomeIcon icon={faPen} className="me-1" /> Manuel
                </button>
              </div>
              {replyMode === 'manual' ? (
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={manualText}
                  placeholder="Merhaba {{first_uye_name}}, {{uye_sehir_da}} mı yaşıyorsun?"
                  onChange={(e) => setManualText(e.target.value)}
                  disabled={sending}
                />
              ) : (
                <div className="brf-chips">
                  {activeList.length === 0 && <span className="text-muted small">Bu grupta hazır mesaj yok.</span>}
                  {activeList.map((msg) => (
                    <button
                      key={msg.id}
                      type="button"
                      className={`brf-chip ${pickedId === msg.id ? 'on' : ''}`}
                      onClick={() => setPickedId(msg.id)}
                      disabled={sending}
                    >
                      {pickedId === msg.id && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                      {msg.title || msg.content || msg.text}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="d-flex gap-2 flex-wrap mb-2">
                <Button size="sm" variant="outline-primary" onClick={() => setSequence((prev) => [...prev, ''])} disabled={sending}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> Satır ekle
                </Button>
                <Form.Select
                  style={{ maxWidth: 280 }}
                  defaultValue=""
                  onChange={(e) => {
                    const msg = (quickMessages || []).find((item) => item.id === e.target.value);
                    addSequenceFrom(msg);
                    e.target.value = '';
                  }}
                  disabled={sending}
                >
                  <option value="">Hazır mesajdan satır ekle</option>
                  {(quickMessages || []).map((msg) => (
                    <option key={msg.id} value={msg.id}>{msg.isSmart ? 'Akıllı: ' : ''}{msg.title || msg.content}</option>
                  ))}
                </Form.Select>
              </div>
              {sequence.map((line, index) => (
                <div className="brf-seq" key={`seq-${index}`}>
                  <span>{index + 1}</span>
                  <Form.Control
                    value={line}
                    placeholder="Merhaba {{first_uye_name}}"
                    onChange={(e) => {
                      const value = e.target.value;
                      setSequence((prev) => prev.map((item, i) => (i === index ? value : item)));
                    }}
                    disabled={sending}
                  />
                  <Button
                    variant="link"
                    className="text-danger p-0"
                    disabled={sending || sequence.length === 1}
                    onClick={() => setSequence((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>
              ))}
            </>
          )}
          <div className="text-muted small mt-2">
            {'{{first_uye_name}}'} ilk isim, {'{{uye_sehir_da}}'} şehrin bulunma hali. Her üyede kendi bilgisiyle dolar.
            {previewText ? ` Örnek: ${previewText}` : ''}
          </div>
        </div>
      </div>
      <div className="brf-foot">
        <div className="brf-preview">
          {progress
            ? `${progress.current}/${progress.total} gönderiliyor · ${progress.name}`
            : result || `${selectedRows.length} sohbete cevap gidecek`}
        </div>
        <div className="d-flex gap-2">
          {sending ? (
            <Button variant="outline-danger" onClick={() => { cancelRef.current = true; }}>Durdur</Button>
          ) : (
            <Button variant="light" onClick={onHide}>İptal</Button>
          )}
          <Button
            variant="primary"
            disabled={sending || selectedRows.length === 0 || templates.length === 0}
            onClick={send}
          >
            {sending ? <Spinner animation="border" size="sm" className="me-2" /> : <FontAwesomeIcon icon={faPaperPlane} className="me-2" />}
            {sending
              ? 'Gönderiliyor...'
              : `${selectedRows.length} sohbete cevap gönder`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
